import { Button, Modal, Image } from 'react-bootstrap';
import useCommonStore, { setIsLoading } from 'store/common';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination } from 'swiper/modules';
import { useEffect, useRef, useState } from 'react';
import {
	useAddAStoryMutation,
	useBucketUrlMutation,
	useStaticAddStoryMutation,
	useUploadFileMutation,
} from 'services';
import { createThumbnailByVideo, getFileExtension } from 'utils';
import 'swiper/css/navigation';
import { useQueryClient } from '@tanstack/react-query';
import { IMAGE_PATHS } from 'constants/imagePaths';
import useAuthStore from 'store/auth';
import { S3_URL } from 'constants/index';
import useTranslate from 'hooks/useTranslate';

interface Slide {
	file: string;
	mediaType: string;
}

const AddStory = () => {
	const { translate } = useTranslate();
	const { modalConfig = {}, hideCommonModal }: any = useCommonStore((state) => state);
	const { data: mediaData = [], id = null } = modalConfig;
	const videoRefs: any = useRef([]);
	const queryClient = useQueryClient();
	const { user: authUser, token }: Record<string, any> = useAuthStore(
		(state) => state.userInfo ?? {},
	);

	const [slides, setSlides] = useState<Slide[]>([]);
	const [thumbnails, setThumbnails] = useState<string[]>([]);

	const { mutateAsync: bucketUrlMutation } = useBucketUrlMutation();
	const { mutateAsync: uploadFileMutation } = useUploadFileMutation();
	const { mutateAsync: addAStoryMutation, isPending } = token
		? useAddAStoryMutation()
		: useStaticAddStoryMutation();

	const onModalClose = () => {
		// Pause all videos when modal closes to prevent AbortError
		videoRefs.current.forEach((video: any) => {
			if (video) {
				video.pause();
			}
		});
		hideCommonModal();
	};
	useEffect(() => {
		if (mediaData?.length > 0) {
			setIsLoading(true);

			const fetchThumbnails = async () => {
				const slidesArray: Slide[] = [];
				const thumbnailsArray: string[] = [];

				for (const file of mediaData) {
					const mediaType = file.type.startsWith('image/') ? 'image' : 'video';
					const thumbnailPromise = new Promise<string>((resolve) => {
						const objectUrl = URL.createObjectURL(file);
						if (mediaType === 'video') {
							createThumbnailByVideo(objectUrl)
								.then((thumbnail) => resolve(thumbnail))
								.catch(() => resolve(''));
						} else {
							resolve(objectUrl);
						}
						slidesArray.push({ file: objectUrl, mediaType });
					});
					const thumbnail = await thumbnailPromise;
					thumbnailsArray.push(thumbnail);
				}

				// Update states after all thumbnails are processed
				setSlides(slidesArray);
				setThumbnails(thumbnailsArray);
				setIsLoading(false);
			};

			(async () => {
				await fetchThumbnails();
			})();
		}
	}, [mediaData]);

	// Cleanup videos when component unmounts
	useEffect(() => {
		return () => {
			videoRefs.current.forEach((video: any) => {
				if (video) {
					video.pause();
				}
			});
		};
	}, []);

	const handleSlideChange = (swiper: any) => {
		// Pause all videos
		videoRefs.current.forEach((video: any, index: number) => {
			if (video) {
				if (swiper?.realIndex !== index) {
					video.pause(); // Pause video if it's not the active slide
				} else {
					// Handle play() promise to prevent AbortError
					const playPromise = video.play();
					if (playPromise !== undefined) {
						playPromise.catch((error) => {
							// Auto-play was prevented or video was removed from DOM
							// This is expected behavior, so we can safely ignore these errors
							if (error.name !== 'AbortError' && error.name !== 'NotAllowedError') {
								console.warn('Video play failed:', error);
							}
						});
					}
				}
			}
		});
	};

	const submitStory = async () => {
		const response = await bucketUrlMutation({
			type: 'STORIES',
			extension: mediaData.map((file: any) => getFileExtension(file?.name)),
			contentType: mediaData.map((file: any) => file.type),
			originalFileName: mediaData.map((file: any) => file.name),
		});
		if (response?.success) {
			setIsLoading(true);
			const uploadData = response?.data?.map((resData: any, index: number) => {
				const { type } = mediaData[index];
				return { ...resData, mediaType: type.startsWith('image/') ? 'image' : 'video' };
			});
			await uploadMedia(uploadData);
			const formData = response?.data?.map((data: any, index: number) => {
				return {
					file: data?.location,
					mediaType: mediaData[index].type.startsWith('image/') ? 'image' : 'video',
				};
			});
			addAStoryMutation({
				media: formData,
			})
				.then(() => {
					queryClient.invalidateQueries({
						queryKey: ['get-stories'],
					});
					queryClient.invalidateQueries({
						queryKey: ['get-static-stories'],
					});
				})
				.finally(() => {
					hideCommonModal();
					setIsLoading(false);
				});
		}
	};

	const uploadMedia = async (values: any) => {
		try {
			const mediaItems = values || [];
			const uploadPromises = mediaItems.map(async (media: any, index: number) => {
				const requestOptions = {
					method: 'PUT',
					headers: {
						'Content-Type': media.mediaType,
					},
					body: mediaData[index],
				};

				return uploadFileMutation({
					url: media.url,
					requestOptions,
				});
			});

			const responses = await Promise.all(uploadPromises);
			// Normalize responses because some upload helpers may return raw fetch Response objects
			const normalized = responses.map((response: any) => {
				if (response && typeof response === 'object' && 'success' in response) return response;
				// Treat fetch Response-like objects as success when ok is true
				return {
					success: !!response?.success || !!response?.ok,
					status: response?.status,
					data: response?.data ?? null,
					raw: response,
				};
			});

			if (normalized.every((r) => r.success)) {
				console.log('All files uploaded successfully:', normalized);
			} else {
				console.error('Some files failed to upload:', normalized);
			}
		} catch (error) {
			console.error('An error occurred during file uploads:', error);
		}
	};

	const pagination = {
		clickable: true,
		renderBullet: (index: number, className: string) => {
			const imgSrc = thumbnails[index];
			return `<span class="${className}"><img src="${imgSrc}" height="50" loading='lazy' /></span>`;
		},
	};

	return (
		<Modal.Body className="p-30">
			<Button className="close-btn rounded-circle fw-bold position-absolute" onClick={onModalClose}>
				<i className="ri-close-line"></i>
			</Button>
			<div className="story-image-slider h-100">
				<Swiper
					navigation={{
						nextEl: '.swiper-button-next',
						prevEl: '.swiper-button-prev',
					}}
					pagination={pagination}
					scrollbar={{ draggable: true }}
					onSwiper={handleSlideChange}
					onSlideChange={handleSlideChange}
					centeredSlides={true}
					slidesPerView={1}
					spaceBetween={30}
					loop={true}
					initialSlide={id !== null ? id : 0}
					modules={[Pagination, Navigation]}
					className="mySwiper"
				>
					{slides.length > 0 &&
						slides.map((slide: any, index: number) => (
							<SwiperSlide key={index}>
								<div className="swiper-slide-inner position-relative d-flex align-items-center">
									{slide.mediaType === 'image' ? (
										<Image src={`${slide?.file}`} alt={`slide-${index}`} loading="lazy" />
									) : (
										<video
											ref={(el) => (videoRefs.current[index] = el)}
											src={`${slide?.file}`}
											controls
											width="100%"
										/>
									)}
									<div className="story-image-slider-post-box d-flex align-items-center justify-content-between">
										<div className="user-story-profile">
											<Image
												src={
													authUser?.profilePicture
														? `${S3_URL}${authUser.profilePicture}`
														: IMAGE_PATHS.UserDefault
												}
												loading="lazy"
											/>
											<span className="text-white">{translate('Modal.yourStory')}</span>
										</div>
										<Button
											disabled={isPending}
											className="post-btn bg-transparent border-0 p-0"
											onClick={submitStory}
										>
											{translate('Modal.post')}
										</Button>
									</div>
								</div>
							</SwiperSlide>
						))}

					<div className="swiper-button-prev"></div>
					<div className="swiper-button-next"></div>
				</Swiper>
			</div>
		</Modal.Body>
	);
};

export default AddStory;
